import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config/storageDatabase';
import { fetchGraphQL } from './fetchGraphQL';

const refreshTokenMutation = `
  mutation RefreshTokenMutation($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
      errors {
        code
        message
      }
    }
  }
`;

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const refreshAccessToken = async (): Promise<boolean> => {
  // Se já está renovando, retorna a promise existente
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      if (typeof window === 'undefined') return false;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return false;
      }

      const response = await fetchGraphQL(
        { text: refreshTokenMutation },
        { refreshToken }
      );

      const errors = response?.data?.refreshToken?.errors;
      const newAccessToken = response?.data?.refreshToken?.accessToken;
      const newRefreshToken = response?.data?.refreshToken?.refreshToken;

      if (errors?.length || !newAccessToken || !newRefreshToken) {
        // Refresh token inválido ou expirado - limpar storage
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        return false;
      }

      // Atualizar tokens no storage
      localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Decodifica JWT para pegar a data de expiração
const getTokenExpiration = ({ token }: { token: string }): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Converter para milliseconds
  } catch {
    return null;
  }
};

// Verifica se o token está próximo de expirar (menos de 1 hora)
export const shouldRefreshToken = (): boolean => {
  if (typeof window === 'undefined') return false;

  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) return false;

  const expiration = getTokenExpiration({ token: accessToken });
  if (!expiration) return false;

  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  // Renovar se faltam menos de 1 hora para expirar
  return expiration - now < oneHour;
};

// Inicia verificação periódica do token
export const startTokenRefreshTimer = () => {
  if (typeof window === 'undefined') return;

  // Verificar a cada 10 minutos
  const interval = setInterval(async () => {
    if (shouldRefreshToken()) {
      const success = await refreshAccessToken();
      if (!success) {
        // Se falhou, redirecionar para login
        window.location.href = '/login';
      }
    }
  }, 10 * 60 * 1000); // 10 minutos

  // Limpar intervalo quando a página for descarregada
  window.addEventListener('beforeunload', () => {
    clearInterval(interval);
  });
};
